import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { ErrorBoundary } from 'react-error-boundary'
import { useTranslation } from 'react-i18next'
import Clarity from '@microsoft/clarity'
import apiClient from '@/lib/api-client'
import { initAnalytics } from '@/utils/analytics'
// Self-hosted variable fonts. Previously pulled from fonts.googleapis.com via
// a <link> in index.html, which made first paint depend on a third-party host.
import '@fontsource-variable/inter'
import '@fontsource-variable/lexend'
import './i18n/config' // Initialize i18n before rendering
import App from './App'
import './index.css'

// Initialize Microsoft Clarity in production only
if (import.meta.env.PROD && import.meta.env.VITE_CLARITY_PROJECT_ID) {
  Clarity.init(import.meta.env.VITE_CLARITY_PROJECT_ID)
}

// Initialize GA4 / Meta Pixel / TikTok Pixel (all gated on env vars).
// Consent Mode v2 defaults keep everything suppressed until cookie consent.
if (import.meta.env.PROD) {
  initAnalytics()
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30 * 1000, // 30 seconds
    },
  },
})

// Invalidate all queries after any mutating API call (POST/PUT/PATCH/DELETE)
// so navigating to another page always shows fresh data
const MUTATING_METHODS = ['post', 'put', 'patch', 'delete'];
apiClient.interceptors.response.use((response) => {
  const method = response.config.method?.toLowerCase();
  if (method && MUTATING_METHODS.includes(method)) {
    // Invalidate all queries — stale ones will refetch when their component mounts
    queryClient.invalidateQueries();
  }
  return response;
})

function ErrorFallback({ error, resetErrorBoundary }: { error: Error; resetErrorBoundary: () => void }) {
  const { t } = useTranslation('common');

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full p-6 bg-white rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold text-red-600 mb-4">{t('errors.somethingWentWrong')}</h2>
        <pre className="text-sm bg-gray-100 p-3 rounded mb-4 overflow-auto">{error.message}</pre>
        <button
          onClick={resetErrorBoundary}
          className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
        >
          {t('actions.tryAgain')}
        </button>
      </div>
    </div>
  )
}

const container = document.getElementById('root')!;

// Reuse the root across HMR reloads. React 18's createRoot never sets
// _reactRootContainer (that was the React 17 legacy API), so keying off it
// mounted a second root on every hot reload and stacked two copies of the app.
const root = container.__reactRoot ?? ReactDOM.createRoot(container);
container.__reactRoot = root;

root.render(
  <React.StrictMode>
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <QueryClientProvider client={queryClient}>
        <App />
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />}
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>,
);

// Extend HTMLElement type to cache the root across HMR
declare global {
  interface HTMLElement {
    __reactRoot?: ReactDOM.Root;
  }
}
